import React, { useEffect, useState } from 'react';
import { Modal, Button } from './ui';
import { OOORequest, User } from '../types';
import { Info, Briefcase, Users } from 'lucide-react';
interface ApprovalModalProps {
  isOpen: boolean;
  onClose: () => void;
  onApprove: (requestId: string, coverageIds: string[]) => void;
  request: OOORequest | null;
  availableUsers: User[];
}
export function ApprovalModal({
  isOpen,
  onClose,
  onApprove,
  request,
  availableUsers
}: ApprovalModalProps) {
  const [selectedCoverage, setSelectedCoverage] = useState<string[]>([]);
  useEffect(() => {
    if (isOpen) {
      setSelectedCoverage([]);
    }
  }, [isOpen]);
  if (!request) return null;
  const isProcessor = request.userRole === 'Processor';
  const isLoanOfficer = request.userRole === 'Loan Officer';
  // Filter available users based on the requester's role
  const eligibleCoverageUsers = availableUsers.filter(u => u.role === request.userRole && u.id !== request.userId);
  const requesterUser = availableUsers.find(u => u.id === request.userId);
  const workloadCount = isProcessor ? requesterUser?.activeLoans || 0 : requesterUser?.pipelineSize || 0;
  const workloadLabel = isProcessor ? 'Active Loans' : 'Pipeline Size';
  const handleToggleUser = (userId: string) => {
    if (isProcessor) {
      // Processors can have multiple covering users
      setSelectedCoverage(prev => prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]);
    } else {
      // Loan Officers have a single designated covering user
      setSelectedCoverage([userId]);
    }
  };
  const handleApprove = () => {
    onApprove(request.id, selectedCoverage);
  };
  const isSubmitDisabled = workloadCount > 0 && selectedCoverage.length === 0;
  return <Modal isOpen={isOpen} onClose={onClose} title="Approve Request & Assign Coverage" maxWidth="max-w-2xl" footer={<>
          <Button variant="neutral" onClick={onClose} data-id="element-59">
            Cancel
          </Button>
          <Button variant="brand" onClick={handleApprove} disabled={isSubmitDisabled} data-id="element-60">
            Approve & Reassign
          </Button>
        </>} data-id="element-58">
      <div className="space-y-6" data-id="element-61">
        {/* Requester Info */}
        <div className="bg-gray-50 p-4 rounded-md border border-gray-200" data-id="element-62">
          <div className="flex items-center justify-between mb-2" data-id="element-63">
            <div data-id="element-64">
              <p className="text-sm text-gray-500" data-id="element-65">Requester</p>
              <p className="font-semibold text-gray-900" data-id="element-66">{request.userName}</p>
            </div>
            <div className="text-right" data-id="element-67">
              <p className="text-sm text-gray-500" data-id="element-68">Role</p>
              <p className="font-medium text-gray-900" data-id="element-69">{request.userRole}</p>
            </div>
          </div>
          <div className="flex items-center justify-between pt-2 border-t border-gray-200" data-id="element-70">
            <div className="flex items-center text-[#0176d3]" data-id="element-71">
              <Briefcase className="w-4 h-4 mr-2" data-id="element-72" />
              <span className="font-medium" data-id="element-73">
                {workloadCount} {workloadLabel}
              </span>
            </div>
          </div>
        </div>

        {/* Coverage Selection */}
        <div data-id="element-74">
          <h4 className="text-base font-medium text-gray-900 mb-3 flex items-center" data-id="element-75">
            <Users className="w-5 h-5 mr-2 text-gray-500" data-id="element-76" />
            {isProcessor ? 'Select Covering Processors' : 'Designate Covering Loan Officer'}
          </h4>

          {workloadCount === 0 ? <div className="bg-blue-50 border border-blue-200 rounded-md p-4 flex items-start" data-id="element-77">
              <Info className="w-5 h-5 text-blue-500 mt-0.5 mr-3 flex-shrink-0" data-id="element-78" />
              <div data-id="element-79">
                <h5 className="text-sm font-medium text-blue-800" data-id="element-80">
                  No Active Workload
                </h5>
                <p className="text-sm text-blue-700 mt-1" data-id="element-81">
                  This {request.userRole.toLowerCase()} currently has no active{' '}
                  {workloadLabel.toLowerCase()}. You can approve this request
                  without assigning coverage.
                </p>
              </div>
            </div> : <div className="border border-gray-200 rounded-md overflow-hidden" data-id="element-82">
              <div className="bg-gray-50 px-4 py-2 border-b border-gray-200" data-id="element-83">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-84">
                  Available {request.userRole}s
                </p>
              </div>
              <ul className="divide-y divide-gray-200 max-h-60 overflow-y-auto" data-id="element-85">
                {eligibleCoverageUsers.length === 0 ? <li className="px-4 py-4 text-sm text-gray-500 text-center" data-id="element-86">
                    No available {request.userRole.toLowerCase()}s found.
                  </li> : eligibleCoverageUsers.map(user => {
              const isSelected = selectedCoverage.includes(user.id);
              const userWorkload = isProcessor ? user.activeLoans : user.pipelineSize;
              return <li key={user.id} className={`px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-gray-50 transition-colors ${isSelected ? 'bg-blue-50/50' : ''}`} onClick={() => handleToggleUser(user.id)} data-id="element-87">
                        <div className="flex items-center" data-id="element-88">
                          <input type={isProcessor ? 'checkbox' : 'radio'} checked={isSelected} onChange={() => {}} // Handled by li onClick
                  className="h-4 w-4 text-[#0176d3] border-gray-300 focus:ring-[#0176d3]" data-id="element-89" />
                          <div className="ml-3" data-id="element-90">
                            <p className="text-sm font-medium text-gray-900" data-id="element-91">
                              {user.name}
                            </p>
                          </div>
                        </div>
                        <div className="text-sm text-gray-500" data-id="element-92">
                          Current {workloadLabel}:{' '}
                          <span className="font-medium text-gray-900" data-id="element-93">
                            {userWorkload || 0}
                          </span>
                        </div>
                      </li>;
            })}
              </ul>
            </div>}
        </div>
      </div>
    </Modal>;
}